<script setup lang="ts">
import { AtomIcon, SatButton } from "@chatfood/design-system";
import { bemBuilder, toMoney } from "@chatfood/core-utils";
import { computed } from "vue";
import type { LoyaltyBalanceButtonProps } from "./props";
import { useCustomerStore } from "~/stores/customer";

const css = bemBuilder("loyalty-balance-button");

const props = withDefaults(defineProps<LoyaltyBalanceButtonProps>(), {
  viewOnly: false,
});

const emits = defineEmits<{ click: [event: MouseEvent] }>();

const customer = useCustomerStore();

const pointsBalance = computed(() => customer.loyalty?.wallet?.points?.balance);
const cashBalance = computed(() => customer.loyalty?.wallet?.cash?.balance);

const loyaltyBalance = computed(() => {
  if (!pointsBalance.value && !cashBalance.value?.value) {
    return undefined;
  }

  return pointsBalance.value > 0
    ? pointsBalance.value
    : toMoney(cashBalance.value.value, cashBalance.value.currency);
});

const iconButtonName = computed(() =>
  cashBalance.value?.value > 0 ? "dollar-sign" : "rating-ghost",
);
</script>

<template>
  <div
    :class="[
      css(''),
      {
        [css('', 'view-only')]: props.viewOnly,
      },
    ]"
  >
    <SatButton
      :class="css('cta')"
      theme="ghost"
      variant="primary"
      @click="emits('click', $event)"
    >
      <AtomIcon :key="iconButtonName" ignore-color :name="iconButtonName" />
      <span v-if="loyaltyBalance" data-test="balance">
        {{ loyaltyBalance }}
      </span>
    </SatButton>
  </div>
</template>

<style lang="scss" scoped>
.loyalty-balance-button {
  $self: &;

  &__cta {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--spacing-sm);
  }

  &--view-only {
    #{$self}__cta {
      cursor: default;
      pointer-events: none;
    }
  }
}
</style>
